// Column-group config that drives both the two-tier grouped header and the
// row cells. Mirrors the manual P&L Google Sheet layout.
// type controls how a cell renders (see TransactionsPnlPage cellContent).

import { ACCOUNTING } from "../../helpers/accountingAccess";

// ── Inline editing ───────────────────────────────────────────────────────────
// A column carrying `edit` can be changed in place, writing straight back to
// the listing via PUT /analytics/transactions/{listing}/fields.
//
//   field — the listings column to write; must also appear in the backend's
//           TransactionsPnlController::FIELD_GRANTS, which is the real gate.
//   kind  — which editor to render (see EditableCell in TransactionsPnlPage).
//   grant — the Accounting grant that unlocks it. No grant → the cell renders
//           read-only exactly as before.
//
// Editors are NATIVE controls (<select>, <input type="date">) on purpose. The
// table sits inside a CSS `zoom` wrapper that scrolls horizontally, and a
// JS-positioned dropdown (react-select and friends) misplaces its menu under
// zoom. Native controls hand the popup to the browser, which gets it right at
// any zoom. The one exception is the supplier picker, which needs async search
// — that opens in a modal outside the zoomed container.
//
// Only fields with one unambiguous writable source are editable. Brand, Model
// and Ref are NOT: they belong to the shared product catalogue, so editing them
// from a finance row would rewrite every store's listing of that watch.
// Supplier City/Country/Company and Client Name are the counterparty's user
// profile, for the same reason. Everything under Revenue, Total, KPI, Status
// and Reconcile is a payment record or a derived figure.

// listings.internal_product → the Stock column's labels (see stockType() in
// OrderExportService; 'no' covers merchant-created listings).
export const STOCK_TYPE_OPTIONS = [
  { value: "owned", label: "Stock" },
  { value: "exclusive", label: "Consignment" },
  { value: "no", label: "Merchant" },
  { value: "to_be_procured", label: "To Be Procured" },
];

// listings.tax_rate. The column header is "Yes/No" — the question is "is this
// standard VAT?", so 'vat' is Yes and margin scheme is No.
export const VAT_OPTIONS = [
  { value: "vat", label: "YES" },
  { value: "margin_scheme", label: "NO" },
];

export const GROUPS = [
  {
    label: "Stock Purchase",
    cols: [
      {
        key: "stock_type", label: "Stock", type: "stockType", w: 90,
        edit: { field: "internal_product", kind: "select", options: STOCK_TYPE_OPTIONS, grant: ACCOUNTING.PNL_EDIT_STOCK },
      },
      {
        key: "buy_date", label: "Purchase Date", type: "text", w: 110,
        edit: { field: "date_purchase", kind: "date", grant: ACCOUNTING.PNL_EDIT_STOCK },
      },
    ],
  },
  {
    label: "Supplier",
    cols: [
      // City and Company come from the supplier's USER record — read-only here;
      // changing them belongs on the user, not on one transaction's row.
      { key: "seller_city", label: "City", type: "text", w: 100 },
      { key: "seller_company", label: "Company Name", type: "text", w: 150 },
      // The picker itself. Hosted on Sales Name because that is the cell showing
      // the supplier's own name, so the value you click is the value you change.
      {
        key: "sales_name", label: "Sales Name", type: "text", w: 120,
        edit: { field: "supplier_id", kind: "supplier", grant: ACCOUNTING.PNL_EDIT_SUPPLIER },
      },
    ],
  },
  {
    label: "Business Introducer",
    cols: [
      { key: "introducer_name", label: "Name", type: "text", w: 120 },
      { key: "introducer_commission", label: "Commission", type: "money", w: 100 },
    ],
  },
  {
    label: "Watch",
    cols: [
      {
        key: "buy_invoice_number", label: "PO/INV Number", type: "text", w: 120,
        edit: { field: "invoice_number", kind: "text", grant: ACCOUNTING.PNL_EDIT_WATCH },
      },
      // The DOCUMENT behind the number to its left. Always shows one of two
      // things and never a bare "—": a "Link" to the paperwork (the Xero PO
      // when the linked Sell Request generated one, otherwise whatever was
      // uploaded into the listing's CONTRACTS section), or an Upload button
      // when there is none. What it uploads is filed in CONTRACTS ▸ Invoices,
      // so removing it there turns the cell back into an Upload button — the
      // link is resolved from the files that exist, never cached on the row.
      { key: "po_invoice_url", label: "PO/Invoice", type: "document", w: 100 },
      // Supplier country sits beside the PURCHASE invoice it belongs to (and
      // buyer country beside the SALE invoice, below) — each country next to
      // the document it describes, rather than stranded in its own group.
      { key: "seller_country", label: "Country", type: "flag", w: 60 },
      { key: "warranty_date", label: "Warranty Date", type: "text", w: 110 },
      { key: "brand", label: "Brand", type: "text", w: 100 },
      { key: "model", label: "Model", type: "text", w: 140 },
      { key: "_img", label: "Watch", type: "image", w: 70 },
      { key: "watch_reference", label: "Ref", type: "link-ref", w: 130 },
      {
        key: "watch_serial_number", label: "Serial Number", type: "text", w: 120,
        edit: { field: "serial_number", kind: "text", grant: ACCOUNTING.PNL_EDIT_WATCH },
      },
      // Free text, not a dropdown: `listings.bank_transfer` has no controlled
      // vocabulary anywhere — its only historical writer is the Excel importer,
      // which stuffs a composed sentence into it. A dropdown would need a bank
      // list that does not exist yet.
      {
        key: "buy_bank", label: "Purchase Bank", type: "text", w: 120,
        edit: { field: "bank_transfer", kind: "text", grant: ACCOUNTING.PNL_EDIT_WATCH },
      },
      { key: "transfer_date", label: "Transfer Date", type: "text", w: 110 },
    ],
  },
  {
    // One buy price per row, shown under whichever currency it was paid in.
    // The editor therefore appears in exactly ONE of these cells — the one
    // matching the row's `buying_currency` (GBP when it has none) — and carries
    // a currency select, so changing the currency moves the figure to another
    // column on the next refresh. PX COG is derived from the trade-in quote, so
    // it stays read-only.
    label: "Buy Price",
    cols: [
      { key: "hkd", label: "HKD", type: "currency", currency: "hkd", w: 90, edit: { field: "buying_price", kind: "buyPrice", grant: ACCOUNTING.PNL_EDIT_BUY_PRICE } },
      { key: "usd", label: "Dollars", type: "currency", currency: "usd", w: 90, edit: { field: "buying_price", kind: "buyPrice", grant: ACCOUNTING.PNL_EDIT_BUY_PRICE } },
      { key: "eur", label: "Euros", type: "currency", currency: "eur", w: 90, edit: { field: "buying_price", kind: "buyPrice", grant: ACCOUNTING.PNL_EDIT_BUY_PRICE } },
      { key: "chf", label: "CHF", type: "currency", currency: "chf", w: 90, edit: { field: "buying_price", kind: "buyPrice", grant: ACCOUNTING.PNL_EDIT_BUY_PRICE } },
      { key: "usdt", label: "USDT", type: "currency", currency: "usdt", w: 90, edit: { field: "buying_price", kind: "buyPrice", grant: ACCOUNTING.PNL_EDIT_BUY_PRICE } },
      { key: "gbp", label: "GBP", type: "gbp", currency: "gbp", w: 100, edit: { field: "buying_price", kind: "buyPrice", grant: ACCOUNTING.PNL_EDIT_BUY_PRICE } },
      { key: "px_cog", label: "PX COG", type: "pxcog", w: 100 },
    ],
  },
  {
    // Exactly the four handling costs captured in the order's "Internal Details
    // & Costs" step (listings.travel/polishing/shipping/accessories), in that
    // section's own order, plus their total. Hotel £, F&B £ and Bank FX £ used
    // to sit here from the manual sheet — nothing in the product ever wrote
    // them, so they could only ever render "—"; removed until real fields exist.
    //
    // `finalisedOnly` blanks the cell until the order's FINALISE COST button has
    // been pressed: an un-finalised row's costs are still being entered, and a
    // "—" there is indistinguishable from a genuine £0.
    label: "Expenses",
    cols: [
      { key: "polishing", label: "Polishing £", type: "money", w: 95, finalisedOnly: true, edit: { field: "polishing", kind: "money", grant: ACCOUNTING.PNL_EDIT_EXPENSES } },
      { key: "accessories", label: "Accessories £", type: "money", w: 95, finalisedOnly: true, edit: { field: "accessories", kind: "money", grant: ACCOUNTING.PNL_EDIT_EXPENSES } },
      { key: "shipping", label: "Shipping £", type: "money", w: 95, finalisedOnly: true, edit: { field: "shipping", kind: "money", grant: ACCOUNTING.PNL_EDIT_EXPENSES } },
      { key: "travel", label: "Transport £", type: "money", w: 95, finalisedOnly: true, edit: { field: "travel", kind: "money", grant: ACCOUNTING.PNL_EDIT_EXPENSES } },
      { key: "_costs_total", label: "Total £", type: "costsTotal", w: 95, finalisedOnly: true },
    ],
  },
  {
    label: "Transaction",
    cols: [
      { key: "order_request_id", label: "Order", type: "link-order-request", w: 110 },
      { key: "sold_via", label: "Sold via", type: "text", w: 90 },
      { key: "buyer_name", label: "Client Name", type: "text", w: 150 },
      { key: "shipment_provider", label: "DHL", type: "text", w: 90 },
      { key: "commission_user_name", label: "Salesperson", type: "text", w: 130 },
      { key: "sell_invoice_number", label: "Invoice N° Xero", type: "text", w: 120 },
      { key: "buyer_country", label: "Country", type: "flag", w: 60 },
      { key: "previs", label: "Previs £", type: "money", w: 100 },
    ],
  },
  {
    label: "Revenue — Deposit",
    cols: [
      { key: "sell_deposit_bank", label: "Bank", type: "text", w: 90 },
      // Bank Transfer / Card / Cash / USDT / POS … (payments.method).
      { key: "sell_deposit_method", label: "Method", type: "text", w: 90 },
      { key: "sell_deposit_date", label: "Date", type: "text", w: 100 },
      { key: "sell_deposit_amount", label: "Deposit £", type: "money", w: 100 },
      { key: "deposit_country", label: "Country", type: "flag", w: 60 },
    ],
  },
  {
    label: "Revenue — Payment",
    cols: [
      { key: "sell_payment_bank", label: "Bank", type: "text", w: 90 },
      { key: "sell_payment_method", label: "Method", type: "text", w: 90 },
      { key: "sell_payment_date", label: "Date", type: "text", w: 100 },
      { key: "sell_payment_amount", label: "Final £", type: "money", w: 100 },
      { key: "transaction_fees", label: "All Trans. Fees", type: "money", w: 110 },
    ],
  },
  {
    label: "VAT",
    cols: [
      {
        key: "tax_rate", label: "Yes/No", type: "text", w: 70,
        edit: { field: "tax_rate", kind: "select", options: VAT_OPTIONS, grant: ACCOUNTING.PNL_EDIT_VAT },
      },
    ],
  },
  {
    label: "Total",
    cols: [
      { key: "order_total", label: "Total £", type: "money", w: 100 },
      // "Refunds" removed: the export has never emitted a `refunds` key and
      // there is no refund record to emit — no Refund model, no relation, only
      // the `refunded` / `refund_requested` order statuses. The column could
      // only ever render "—". Restoring it needs a refund amount to exist first.
    ],
  },
  {
    label: "KPI",
    cols: [
      { key: "previs", label: "Previs.", type: "money", w: 100 },
      // Booked margin on a settled sale; on a DEPOSITED one, the provisional
      // margin the deal is on course to make (Previs in place of cash received)
      // — otherwise the cell shows a large negative that only reflects the
      // balance still to arrive. The basis is rendered under the figure, since
      // the two are not comparable and share one column. The backend picks
      // which (income_gross_basis); see incomeGrossShown in TransactionsPnlPage.
      { key: "income_gross", label: "Income Gross", type: "incomeGross", w: 120 },
      // VAT owed to HMRC on the sale. Which basis produced it comes from the
      // listing's Margin Scheme / VAT toggle — rendered under the figure, since
      // a bare number's meaning otherwise depends on the VAT column far to the
      // left. Blank on PX trade-in rows (a purchase has no tax point yet) and
      // on any sale that is not domestic (export / import / outside scope).
      { key: "vat_owed", label: "Margin Scheme / VAT", type: "vatOwed", w: 150 },
      { key: "commissions_detail", label: "Commissions", type: "commissions", w: 240 },
      // Expert fee (PAYMENTS step). Its OWN column rather than folded into
      // Commissions, so each column still equals the records that produced it.
      // An order-level fee is split across the order's watches by outstanding
      // balance, so the footer total equals what was actually paid — see
      // OrderExportService::orderReceiptAllocation.
      { key: "expert_fee", label: "Expert Fees", type: "money", w: 110 },
    ],
  },
  {
    label: "",
    cols: [
      { key: "order_or_px_request_status", label: "Status", type: "status", w: 110 },
      { key: "revenue_reconciled", label: "Revenue Reconcile", type: "reconciliation", w: 130 },
      { key: "purchase_reconciled", label: "Purchase Reconcile", type: "purchaseReconciliation", w: 130 },
      { key: "cost_reconciled", label: "Costs Reconcile", type: "costReconciliation", w: 130 },
      // Free-text note per watch row, saved on the listing (listings.pnl_note)
      // so it is shared with the team rather than living in one browser. PX
      // trade-in rows carry their own note — they are their own listing.
      { key: "pnl_note", label: "Notes", type: "note", w: 200 },
    ],
  },
];

// Flatten, tagging the last column of each group so the grid can draw a
// stronger vertical divider after each group band.
export const FLAT_COLS = GROUPS.flatMap((g) =>
  g.cols.map((c, i) => ({ ...c, groupEnd: i === g.cols.length - 1 }))
);

/**
 * Columns hidden when the store's plan lacks a feature (Feature 42, Alex
 * 2026-08-13: no commissions split ⇒ no Commissions column in P&L). The
 * underlying figures are untouched — commissions already recorded keep
 * counting toward profit; this only stops showing the breakdown column.
 */
const FEATURE_COLUMNS = {
  commissions_split: ["commissions_detail"],
};

/**
 * Build the group/column set for a store, dropping columns whose feature the
 * plan doesn't include. `groupEnd` is recomputed AFTER filtering so the
 * group-divider border still lands on the last visible column.
 */
export const buildColumns = (hasFeature) => {
  const hidden = new Set(
    Object.entries(FEATURE_COLUMNS)
      .filter(([feature]) => !hasFeature(feature))
      .flatMap(([, keys]) => keys)
  );

  const groups = GROUPS
    .map((g) => ({ ...g, cols: g.cols.filter((c) => !hidden.has(c.key)) }))
    .filter((g) => g.cols.length > 0);

  const flatCols = groups.flatMap((g) =>
    g.cols.map((c, i) => ({ ...c, groupEnd: i === g.cols.length - 1 }))
  );

  return { groups, flatCols };
};
